"use client";

import { useTranslations } from "next-intl";
import { FIELD, PORTAL_OVERLAY } from "@/components/creatorPortal/one/chrome";
import { Input } from "@/components/ui/design-system/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/design-system/select";
import {
  type ApplyFormErrorKey,
  type ApplyFormState,
  MAX_EMAIL_LENGTH,
  MAX_SHORT_TEXT_LENGTH,
} from "./applyForm";
import { Field } from "./applyFormPrimitives";

/**
 * Steps take the whole form value plus a setter rather than owning their
 * own state: this is one form with one submit, and a step holding a
 * private copy would have to be reconciled on every keystroke. (The
 * own-your-state rule in the frontend guide is about dialogs, which open and
 * close around a parent that keeps refetching.)
 */
export function ApplyAboutYouStep({
  form,
  ageRanges,
  errorFor,
  onChange,
}: {
  form: ApplyFormState;
  ageRanges: readonly string[];
  errorFor: (key: ApplyFormErrorKey) => string | undefined;
  onChange: <K extends keyof ApplyFormState>(
    key: K,
    value: ApplyFormState[K],
  ) => void;
}) {
  const t = useTranslations("apply");
  return (
    <div className="flex flex-col gap-nf-ds-5">
      <Field label={t("fields.name.label")} error={errorFor("name")}>
        <Input
          value={form.name}
          onChange={(e) => onChange("name", e.target.value)}
          maxLength={MAX_SHORT_TEXT_LENGTH}
          autoComplete="name"
          placeholder={t("fields.name.placeholder")}
        />
      </Field>

      <Field
        label={t("fields.email.label")}
        hint={t("fields.email.hint")}
        error={errorFor("email")}
      >
        <Input
          type="email"
          value={form.email}
          onChange={(e) => onChange("email", e.target.value)}
          maxLength={MAX_EMAIL_LENGTH}
          autoComplete="email"
          placeholder={t("fields.email.placeholder")}
        />
      </Field>

      <div className="grid gap-nf-ds-5 sm:grid-cols-2">
        <Field label={t("fields.ageRange.label")} error={errorFor("ageRange")}>
          <Select
            value={form.ageRange}
            onValueChange={(value) => onChange("ageRange", value)}
          >
            <SelectTrigger className={FIELD}>
              <SelectValue placeholder={t("fields.ageRange.placeholder")} />
            </SelectTrigger>
            <SelectContent className={PORTAL_OVERLAY}>
              {ageRanges.map((range) => (
                <SelectItem key={range} value={range}>
                  {range}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("fields.location.label")} hint={t("fields.optional")}>
          <Input
            value={form.location}
            onChange={(e) => onChange("location", e.target.value)}
            maxLength={MAX_SHORT_TEXT_LENGTH}
            placeholder={t("fields.location.placeholder")}
          />
        </Field>
      </div>
    </div>
  );
}
